/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import i18n from 'i18next';

// Misc imports
import api from '../utils/apiUtils/api';
import { handler, socketApi } from '../../socket';
import { livechatActions } from './liveChatSlice';
import { INCOMING_EVENTS, OUTGOING_EVENTS } from '../../constants/liveChat';
import is from '../../utils/is';
import satisfiesInboxFilters from '../../utils/misc/satisfiesInboxFilters';
import { uniqueBy } from '../../utils/array/uniqueBy';
import { addConversationMessage, onReconnectSocket, removeConversation, updateConversationOnMessage } from './liveChatActions';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

export const establishConnection = navigate => (dispatch, getState) => {
  dispatch(livechatActions.establishConnectionStart());

  const onConnectError = (error) => {
    dispatch(livechatActions.establishConnectionFail({ error: error.type }));
    dispatch(livechatActions.authorizeConnectionFail({ error: error.type }));
  };
  const onReconnect = () => {
    onReconnectSocket();
  };

  socketApi.applyEvent('connect', () => {
    dispatch(livechatActions.establishConnectionComplete());
    dispatch(livechatActions.authorizeConnectionStart());
  });

  socketApi.applyEvent('event', (event) => {
    const { type, data } = event;
    if (!type || !(type in handler)) {
      console.error('Undefined event type: ' + type);
      return;
    }
    handler[type](data, navigate, event?.metadata);
  });

  socketApi.applyEvent('message', (message) => {
    const activeConversationId = getState().livechat?.activeConversationInfo?.id;

    if (message) {
      if (activeConversationId === message.livechatConversation.id) {
        dispatch(readConversation(message.livechatConversation.id));
      }

      addConversationMessage(message);

      if (satisfiesInboxFilters(message.livechatConversation)) {
        updateConversationOnMessage(message);
      } else {
        removeConversation(message.livechatConversation);
      }

      const sound = document.getElementById('hbfNotificationSound');
      const hasFocus = document.hasFocus();

      if (
        sound
        && getState().livechat.conversationsSoundEnabled
        && message.livechatConversation.livechatParticipants.find(participant => participant.platformUserId === getState().auth.currentUser.id)
      ) {
        if (
          getState().livechat.activeConversationInfo?.id !== message.livechatConversation.id
          || (getState().livechat.activeConversationInfo?.id === message.livechatConversation.id && document.getElementById('hbfLiveChatSendbox') !== document.activeElement)
          || document.hidden
          || !hasFocus
        ) {
          sound.play();
        }
      }
    }
  });

  try {
    socketApi.connect(getState().auth.token, getState().auth.organizationId, onConnectError, onReconnect);
  } catch (error) {
    onConnectError(error);
  }
};

export const acceptRequest = (data) => async dispatch => {
  try {
    const result = await socketApi.emit('event', data);
    if (result && result.type === INCOMING_EVENTS.ERROR) {
      handler[result.type](result.data);
    }
  } catch (error) {
    dispatch(livechatActions.acceptRequestFail(error));
    openConsoleAlertMessage({ type: 'error', message: i18n.t('An error occurred while accepting the request.') });
  }
};

export const sendConversationMessage = (data) => async dispatch => {
  try {
    socketApi.emit('message', data);
  } catch (error) {
    dispatch(livechatActions.sendMessageFail(error));
    openConsoleAlertMessage({ type: 'error', message: i18n.t('Failed to send message.') });
  }
};

export const transferConversation = (data, isPublicMode = false) => async dispatch => {
  dispatch(livechatActions.transferConversationStart());

  try {
    const result = await socketApi.emit('event', data);
    if (!result || result?.type === INCOMING_EVENTS.ERROR) {
      dispatch(livechatActions.transferConversationFail());
    }
  } catch (error) {
    dispatch(livechatActions.transferConversationFail(error));
    openConsoleAlertMessage({ type: 'error', message: isPublicMode ? i18n.t('Failed to invite user.') : i18n.t('Failed to transfer the conversation.') });
  }
};

export const terminateConversation = (id) => async () => {
  try {
    const data = {
      type: OUTGOING_EVENTS.CONVERSATION_END,
      data: { id }
    };
    socketApi.emit('event', data);
  } catch (error) {
    openConsoleAlertMessage({ type: 'error', message: i18n.t('Failed to terminate the conversation.') });
  }
};

export const leaveConversation = (id) => async () => {
  try {
    const data = {
      type: OUTGOING_EVENTS.CONVERSATION_LEAVE,
      data: {
        id
      }
    };
    socketApi.emit('event', data);
  } catch (error) {
    openConsoleAlertMessage({ type: 'error', message: i18n.t('Failed to leave the conversation.') });
  }
};

export const readConversation = (id) => async (_dispatch, getState) => {
  socketApi.emit('event', {
    type: OUTGOING_EVENTS.CONVERSATION_READ,
    data: {
      id,
      organizationId: getState().auth.organizationId
    }
  });
};

export const joinConversation = id => {
  socketApi.emit('event', {
    type: OUTGOING_EVENTS.CONVERSATION_JOIN,
    data: { id }
  });
};

export const sendTypingEvent = (id) => {
  try {
    socketApi.emit('event', {
      type: OUTGOING_EVENTS.CONVERSATION_TYPING,
      data: { id }
    });
  } catch (error) {
    console.error(error);
  }
};

export const uploadAttachments = (data, options) => async dispatch => {
  dispatch(livechatActions.uploadFilesStart());

  try {
    await api.uploadAttachments(data, options);
    dispatch(livechatActions.uploadFilesComplete());

  } catch (error) {
    dispatch(livechatActions.uploadFilesFail(error));
    openConsoleAlertMessage({ type: 'error', message: i18n.t('An error occurred while uploading the files.') });
  }
};

export const togglePushNotifications = (permission) => dispatch => {
  dispatch(livechatActions.togglePushNotifications(permission));
};

export const toggleNotificationSound = (checked, muteRequests = false) => dispatch => {
  if (muteRequests) {
    dispatch(livechatActions.toggleRequestsSound(checked));
  } else {
    dispatch(livechatActions.toggleConversationsSound(checked));
  }
};

export const setSendMessageMode = (mode) => dispatch => {
  dispatch(livechatActions.setSendMessageMode(mode));
};

export const setSpellCheckLanguage = (language) => dispatch => {
  dispatch(livechatActions.setSpellCheckLanguage(language));
};

export const updateFavoriteAutomations = (values) => async dispatch => {
  const updatedList = uniqueBy(values.map(automationGroup => {
    const { orgId, automations } = automationGroup;
    return { orgId, automations };
  }), 'orgId');

  dispatch(livechatActions.setFavoriteAutomations(updatedList));
};

export const setInboxFilters = (filters) => async dispatch => {
  if (is.object(filters)) {
    dispatch(livechatActions.setInboxFilters(filters));
  }
};

export const resetInboxFilters = () => async dispatch => {
  dispatch(livechatActions.resetInboxFilters());
};

export const disconnectLivechatSocket = () => dispatch => {
  try {
    if (socketApi.socket) {
      socketApi.disconnect();
      dispatch(livechatActions.disconnectLivechatSocket());
    }
  } catch (error) {
    console.error(error);
  }
};

export const markAsImportant = (id, operation, flag) => {
  try {
    socketApi.emit('event', {
      type: OUTGOING_EVENTS.PARTICIPANT_FLAG,
      data: {
        conversation: id,
        operation: operation,
        flag: flag
      }
    });
  } catch (error) {
    openConsoleAlertMessage({ type: 'error', message: i18n.t('Failed to mark conversation as important.') });
  }
};
